﻿import CONSTANTS from '../utils/constants.js';
import { getProgress, saveProgress, clearProgress } from './session-manager.js';
import { toggleModalVisibility } from '../utils/modals.js';
import { withQueryParams } from '../utils/general.js';

const SELECTORS = Object.freeze({
    desktopContainer: '.breadcrumbs__desktop-wrapper .breadcrumbs__list',
    mobilePanelContainer: '.breadcrumbs__mobile-wrapper .breadcrumbs__list-mobile',
    header: '.js-breadcrumbs-header',
    toggle: '.js-breadcrumbs-toggle',
    panel: '.js-breadcrumbs-panel',
    currentStep: '.js-breadcrumbs-current-step',
});

const DEFAULT_STEPS = Object.freeze([
    {
        id: 0,
        name: 'calculator',
        url: '/quote?query=false',
        labelKey: 'Calculator',
        isAccessible: true,
        formDirtyKey: 'calculatorFormDirty' 
    },
    {
        id: 1,
        name: 'services',
        url: '/services',
        labelKey: 'Services',
        isAccessible: true,
        formDirtyKey: 'servicesFormDirty' 
    },
    {
        id: 2,
        name: 'additional-details',
        url: '/shipdetails',
        labelKey: 'AdditionalDetails',
        isAccessible: false,
        formDirtyKey: 'additionalDetailsFormDirty'
    },
    {
        id: 3,
        name: 'summary',
        url: '/checkout',
        labelKey: 'Summary',
        isAccessible: false,
        formDirtyKey: 'summaryFormDirty'
    },
    {
        id: 4,
        name: 'waybill',
        url: '/success',
        labelKey: 'Waybill',
        isAccessible: false,
        formDirtyKey: null 
    },
]);

const isNewSession = (currentPath) => currentPath === '/success';

const updateProgress = (progressKey, currentPath) => {
    let progress = getProgress(progressKey);
    const normalizedPath = currentPath.toLowerCase();

    if (isNewSession(normalizedPath)) {
        clearProgress(progressKey);
        progress = getProgress(progressKey);
    }
    return progress;
};

const getIndexByCode = (code) => {
    switch (code) {
        case 'calculator':
            return 0;
        case 'services':
            return 1;
        case 'additional-details':
            return 2;
        case 'summary':
            return 3;
        case 'waybill':
            return 4;
        default:
            return null;
    }
}

const getPageIndex = (path) => {
    switch (path) {
        case '/quote?query=false':
            return 0;
        case '/services':
            const hasResultTable = document.querySelector('.filter-results-table');
            return hasResultTable ? 1 : 0;
        case '/shipdetails':
            return 2;
        case '/checkout':
            return 3;
        case '/waybill':
            return 4;
        default:
            return null;
    }
}

const getStepAccessibility = (step, progress, currentStepIndex) => {
    switch (step.name) {
        case 'calculator':
            return true;
        case 'services':
            return progress.ServicesAccessible === true;
        case 'additional-details':
            return progress.AdditionalDetailsAccessible === true && (currentStepIndex > getIndexByCode(step.name) ? true : progress.servicesFormDirty === false);
        case 'summary':
            return progress.SummaryAccessible === true && progress.servicesFormDirty === false && progress.additionalDetailsFormDirty === false;
        case 'waybill':
            return progress.WaybillAccessible === true;
        default:
           return step.isAccessible;
    }
};

const createStepElement = (step, index, isActive, isAccessible, translations) => {
    const isStepAccessible = isAccessible && !isActive;
    const stepClasses = [
        'breadcrumbs__step',
        isActive ? 'breadcrumbs__step--active' : '',
        isStepAccessible ? 'breadcrumbs__step--accessible' : '',
        !isStepAccessible && !isActive ? 'breadcrumbs__step--disabled' : '',
    ].filter(Boolean).join(' ');

    const label = translations[step.labelKey] || step.labelKey || step.name;

    const li = document.createElement('li');
    li.className = 'breadcrumbs__item';

    const button = document.createElement('button');
    button.className = `button ${stepClasses}`;
    button.setAttribute('type', 'submit');
    button.dataset.step = step.labelKey;
    button.disabled = !isStepAccessible;
    button.setAttribute('aria-disabled', !isStepAccessible);

    button.addEventListener('click', (event) => {
        if (isStepAccessible) {
            button.setAttribute('formaction', withQueryParams(step.url));
        }
    });

    const circle = document.createElement('div');
    circle.className = 'button--circle breadcrumbs__circle';
    circle.setAttribute('aria-hidden', 'true');
    circle.innerHTML =
        index + 1 > 0
            ? `${index + 1}`
            : '<i class="fa-solid fa-chevron-left"></i>';

    const labelSpan = document.createElement('span');
    labelSpan.className = 'breadcrumbs__label';
    labelSpan.textContent = label;

    button.appendChild(circle);
    button.appendChild(labelSpan);
    li.appendChild(button);

    return li;
};

const createToggleCircles = (steps, currentStepIndex, translations) => {
    const container = document.createElement('div');
    container.className = 'breadcrumbs__toggle-circles';
    steps.forEach((step, index) => {
        const circle = document.createElement('div');
        circle.className = 'button--circle breadcrumbs__toggle-circle';
        circle.setAttribute('aria-hidden', 'true');
        circle.innerHTML = `${index + 1}`;
        container.appendChild(circle);
        if (index === currentStepIndex) {
            circle.classList.add('breadcrumbs__toggle-circle--active');
            const label = document.createElement('span');
            label.className = 'breadcrumbs__toggle-label';
            label.textContent = translations[step.labelKey] || step.labelKey || step.name;
            container.appendChild(label);
        }
    });
    return container;
};

const getPanelHeight = (panel) => {
    return `${panel.scrollHeight}px`;
};

class Breadcrumbs {
    constructor(translations, steps = DEFAULT_STEPS, progressKey = CONSTANTS.PROGRESS_KEY) {
        this.desktopContainer = document.querySelector(SELECTORS.desktopContainer);
        this.mobilePanelContainer = document.querySelector(SELECTORS.mobilePanelContainer);
        this.translations = translations || {};
        this.steps = steps;
        this.progressKey = progressKey;
        this.selectors = SELECTORS;
    }

    init() {
        this.render();
        this.setAccordionStates(false);
        this.bindAccordionEvents();
        this.bindOutsideClickListener();
        window.addEventListener('resize', () => this.handleResize());
    }

    handleResize() {
        this.render();
    }

    render() {
        const progress = updateProgress(this.progressKey, window.location.pathname);
        const currentPath = window.location.pathname.toLowerCase();
        const currentStepIndex = getPageIndex(currentPath);

        if (this.desktopContainer) {
            this.desktopContainer.innerHTML = '';
            this.steps.forEach((step, index) => {
                const isActive = index === currentStepIndex;
                const isAccessible = getStepAccessibility(step, progress, currentStepIndex);
                const stepElement = createStepElement(step, index, isActive, isAccessible, this.translations);
                this.desktopContainer.appendChild(stepElement);
            });
        }

        if (this.mobilePanelContainer) {
            this.mobilePanelContainer.innerHTML = '';
            this.steps.forEach((step, index) => {
                const isActive = index === currentStepIndex;
                const isAccessible = getStepAccessibility(step, progress, currentStepIndex);
                const stepElement = createStepElement(step, index, isActive, isAccessible, this.translations);
                this.mobilePanelContainer.appendChild(stepElement);
            });
        }

        const currentStepContainer = document.querySelector(this.selectors.currentStep);
        if (currentStepContainer) {
            currentStepContainer.innerHTML = '';
            const toggleCircles = createToggleCircles(this.steps, currentStepIndex, this.translations);
            currentStepContainer.appendChild(toggleCircles);
        }
    }

    bindOutsideClickListener() {
        const accordionContainer = document.querySelector('.accordion-container--breadcrumbs');
        if (!accordionContainer) {
            console.error('Accordion container not found.');
            return;
        }

        document.addEventListener('click', (event) => {
            const isClickInside = accordionContainer.contains(event.target);
            if (!isClickInside) {
                const header = document.querySelector(this.selectors.header);
                if (header && header.classList.contains('active')) {
                    this.setAccordionStates(false);
                }
            }
        });
    }

    setAccordionStates(shouldOpen) {
        const header = document.querySelector(this.selectors.header);
        const panel = document.querySelector(this.selectors.panel);
        const toggle = document.querySelector(this.selectors.toggle);

        if (!header || !panel || !toggle) {
            console.error('Required accordion elements not found');
            return;
        }
        header.classList.toggle('active', shouldOpen);

        if (shouldOpen) {
            const panelHeight = getPanelHeight(panel);
            panel.style.maxHeight = panelHeight;
            toggle.querySelector('.chevron').classList.replace('fa-chevron-down', 'fa-chevron-up');
        } else {
            panel.style.maxHeight = 0;
            toggle.querySelector('.chevron').classList.replace('fa-chevron-up', 'fa-chevron-down');
        }

    }

    bindAccordionEvents() {

        const self = this;
        const header = document.querySelector(this.selectors.header);
        const toggle = document.querySelector(this.selectors.toggle);
        const panel = document.querySelector(this.selectors.panel);

        if (!header || !toggle || !panel) {
            console.error('Accordion elements missing:', {
                header: !!header,
                toggle: !!toggle,
                panel: !!panel,
            });
            return;
        }
        if (toggle) {
            toggle.addEventListener('click', (e) => {
                e.preventDefault();
                const isCurrentlyOpen = header.classList.contains('active');
                const shouldOpen = !isCurrentlyOpen;
                self.setAccordionStates(shouldOpen);
            });
        }
    }

    getCurrentStepIndex(currentPath) {
        //TODO: probably not needed
        const normalizedPath = currentPath.toLowerCase();
        for (let i = 0; i < this.steps.length; i++) {
            const step = this.steps[i];
            const stepUrlNormalized = step.url.toLowerCase();
            if (step.name === 'calculator' && (normalizedPath === '/' || normalizedPath === '/quote')) {
                return i;
            }
            if (stepUrlNormalized === normalizedPath) {
                return i;
            }
        }
        return -1;
    }
}

let breadcrumbsInstance = null;

function initBreadcrumbs(callback) {
    if (breadcrumbsInstance) {
        callback?.(breadcrumbsInstance);
        return breadcrumbsInstance;
    }

    const list = document.querySelector(SELECTORS.desktopContainer);
    if (!list) return null;

    try {
        const translations = JSON.parse(list.dataset.translations || '{}');
        breadcrumbsInstance = new Breadcrumbs(translations);
        breadcrumbsInstance.init();
        if (typeof callback === 'function') {
            callback(breadcrumbsInstance);
        }
    } catch (error) {
        console.error('Failed to parse translations:', error);
    }
    return breadcrumbsInstance;
}

export { initBreadcrumbs, breadcrumbsInstance };